import React, { useState, useEffect } from 'react';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Wrapper minimal autour du SDK Square Web Payments.
 * Important : ne JAMAIS mettre de contenu lourd (SVG, gradients, animations)
 * AVANT le <PaymentForm> dans ce composant — ça perturbe le timing de montage
 * de l'iframe Square. Pour afficher un bandeau "paiement sécurisé", le mettre
 * dans le parent (la page checkout) au-dessus de <SquarePaymentForm />.
 */
export function SquarePaymentForm({ amount, onSuccess, onError, disabled = false, buttonText = "Payer", buyerEmail = "", buyerName = "" }) {
  const [squareConfig, setSquareConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Arrondi propre pour eviter les artefacts de virgule flottante
  // (ex : 0.89 + 5 = 5.890000000000001 en JS)
  const displayAmount = (Math.round((Number(amount) || 0) * 100) / 100).toFixed(2);

  // Détecte les contextes Web problématiques (cookies tiers, popup, etc.)
  const detectBrowserIssues = () => {
    const issues = [];
    try {
      // Test cookies tiers (Square iframe en a besoin)
      document.cookie = "sq_test=1; SameSite=None; Secure";
      const hasCookies = document.cookie.includes("sq_test=1");
      if (!hasCookies) issues.push("cookies_blocked");
      // Test navigation privée (chunk d'API limitée)
      if (typeof window !== "undefined" && window.navigator && window.navigator.userAgent) {
        const ua = window.navigator.userAgent;
        if (/Android/i.test(ua) && /Chrome/i.test(ua)) issues.push("chrome_android");
      }
    } catch (e) { issues.push("storage_blocked"); }
    return issues;
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/api/payments/config`);
        setSquareConfig(res.data);
      } catch (e) {
        console.error("Failed to load Square config:", e);
        setLastError({ kind: "config_load_failed", message: e?.message || "Impossible de charger le formulaire de paiement." });
      }
    };
    fetchConfig();
  }, []);

  // Log les erreurs côté backend pour diagnostic ultérieur
  const logPaymentError = async (kind, details) => {
    try {
      await axios.post(`${API}/api/payments/log-error`, {
        kind,
        details: typeof details === "string" ? details : JSON.stringify(details).slice(0, 1500),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        browser_issues: detectBrowserIssues(),
        amount: displayAmount,
        buyer_email: buyerEmail || null,
      });
    } catch (_) { /* silent */ }
  };

  if (lastError && lastError.kind === "config_load_failed") {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
        ⚠️ Le formulaire de paiement n'a pas pu être chargé. Vérifie ta connexion ou désactive ton bloqueur de publicité, puis recharge la page.
        Sinon, contacte-nous à <a href="mailto:guillaume@bilan2vie.fr" className="underline text-[#D926A9]">guillaume@bilan2vie.fr</a> ou
        au <a href="tel:+33768489056" className="underline text-[#D926A9]">07 68 48 90 56</a>.
      </div>
    );
  }
  if (!squareConfig || !squareConfig.applicationId) {
    return (
      <div className="text-center py-4 text-sm text-[#B59ED1]">
        Chargement du formulaire de paiement...
      </div>
    );
  }

  return (
    <div data-testid="square-payment-form" className="square-form-wrapper">
      <PaymentForm
        applicationId={squareConfig.applicationId}
        locationId={squareConfig.locationId}
        cardTokenizeResponseReceived={async (token, verifiedBuyer) => {
          if (processing || disabled) return;
          // Cas où Square retourne une erreur dans le token (tokenization échouée)
          if (token?.status !== "OK" || (token?.errors && token.errors.length)) {
            const errMsg = token?.errors?.[0]?.message || "Tokenization échouée";
            setLastError({ kind: "tokenize_failed", message: errMsg });
            await logPaymentError("tokenize_failed", { token_status: token?.status, errors: token?.errors });
            toast.error(`Erreur carte : ${errMsg}`);
            return;
          }
          setProcessing(true);
          try {
            // verifiedBuyer.token = verification token retourné par Square après SCA / 3DS
            // (obligatoire pour les paiements e-commerce en Europe - DSP2)
            const verificationToken = verifiedBuyer?.token || token.verificationToken || null;
            if (!verificationToken) {
              // 3DS pas complété — la banque a refusé ou l'utilisateur a annulé
              await logPaymentError("verify_buyer_failed", { reason: "no_verification_token" });
            }
            await onSuccess(token.token, verificationToken);
            setLastError(null);
          } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Paiement refusé";
            setLastError({ kind: "backend_payment_failed", message: msg });
            await logPaymentError("backend_payment_failed", msg);
            if (onError) onError(e); else toast.error(msg);
          } finally {
            setProcessing(false);
          }
        }}
        createPaymentRequest={() => ({
          countryCode: "FR",
          currencyCode: "EUR",
          total: {
            amount: displayAmount,
            label: "Total",
          },
        })}
        createVerificationDetails={() => ({
          amount: displayAmount,
          currencyCode: "EUR",
          intent: "CHARGE",
          billingContact: {
            familyName: (buyerName || "").split(" ").slice(1).join(" ") || "",
            givenName: (buyerName || "").split(" ")[0] || "",
            email: buyerEmail || undefined,
            countryCode: "FR",
          },
        })}
      >
        <CreditCard
          buttonProps={{
            isLoading: processing || disabled,
            css: {
              backgroundColor: "#D926A9",
              color: "#FFFFFF",
              fontSize: "15px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#c01f96" },
              "&:active": { backgroundColor: "#a91a82" },
            },
          }}
          style={{
            // ⚠️ NE PAS mettre backgroundColor ni color-scheme ici — le SDK Square 1.83.9+
            // reste figé (iframe height:0, wrapper hidden) si on lui passe ces propriétés.
            // Bug observé en prod le 26/06/2026 avec react-square-web-payments-sdk 3.3.0.
            // On se contente de bordures + couleur de texte pour rester dans une zone safe.
            ".input-container": { borderColor: "#3A2952", borderRadius: "8px" },
            ".input-container.is-focus": { borderColor: "#D926A9" },
            ".input-container.is-error": { borderColor: "#ef4444" },
            input: { color: "#1B132B", fontSize: "16px" },
            ".message-text": { color: "#6b7280" },
            ".message-text.is-error": { color: "#dc2626" },
          }}
        >
          {processing ? "Traitement..." : `${buttonText} ${displayAmount} €`}
        </CreditCard>
      </PaymentForm>

      {/* Message d'aide en cas d'échec récent */}
      {lastError && (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3.5 text-xs text-red-200" data-testid="square-payment-error">
          <p className="font-semibold mb-1.5">⚠️ Le paiement n'a pas pu aboutir</p>
          <p className="text-red-200/80 mb-2">{lastError.message}</p>
          <p className="text-[#E2D5F8]/80 leading-relaxed">
            <strong>Que faire ?</strong> Essaye une autre carte, désactive ton VPN/bloqueur de publicité, ou réessaie depuis un autre navigateur.
            Si le problème persiste, contacte-moi directement :{" "}
            <a href="mailto:guillaume@bilan2vie.fr" className="underline text-[#D926A9]">guillaume@bilan2vie.fr</a> · {" "}
            <a href="tel:+33768489056" className="underline text-[#D926A9]">07 68 48 90 56</a>.
          </p>
        </div>
      )}
    </div>
  );
}
